import { DrawerSize, Icon, Position } from "@blueprintjs/core"
import CustomDrawer, { CustomDrawerProps } from "components/Atoms/CustomDrawer"
import CustomTag from "components/Atoms/CustomTag"
import StaticSelector from "components/Atoms/StaticSelector"
import { useVendorSummary } from "graphql/vendor/vendor.hooks"
import React, { useState } from "react"
import { useFilter } from "store/search-filter/search-filter.reducer"
import { DAY_INTERVALS, USER_STATUSES } from "data/static.items"
import InwardSummaryCard from "../../weighing/summary/InwardSummaryCard"

interface VendorSummaryDrawerProps extends CustomDrawerProps {}
const VendorSummaryDrawer = ({
  isOpen,
  onClose,
  ...otherProps
}: VendorSummaryDrawerProps) => {
  const [days, setDays] = useState({
    id: 7,
    name: "7 Days",
  })
  const arr = useFilter("vendors")
  const setFilter = arr[1]
  const { data } = useVendorSummary(days.id)
  const summary = data?.summaryUsersEcom

  const total = summary?.totalCustomers ?? 0
  const active = summary?.activeCustomers ?? 0
  const inactive = summary?.inactiveCustomers ?? 0
  const verified = summary?.verifiedCustomers ?? 0
  const unverified = summary?.notVerifiedCustomers ?? 0

  return (
    <CustomDrawer
      drawerClassName="vt-inward-summary-drawer-popover"
      position={Position.TOP}
      icon="pie-chart"
      title={
        <div className="rg-vendor-summary">
          <span className="text-muted">Vendor Summary </span>
          <StaticSelector
            className="mb-0"
            value={days}
            onChange={(_: any, value: any) => setDays(value)}
            items={DAY_INTERVALS}
          >
            {(value: any) => {
              return (
                <CustomTag className="ml-3" minimal interactive>
                  {value?.name} <Icon iconSize={12} icon="chevron-down" />
                </CustomTag>
              )
            }}
          </StaticSelector>
        </div>
      }
      size={DrawerSize.SMALL}
      className="p-3 vt-inward-summary-drawer"
      isOpen={isOpen}
      onClose={onClose}
      {...otherProps}
    >
      {summary ? (
        <div className="flex h-full w-full">
          <div className="w-full">
            <InwardSummaryCard
              intent={{
                danger: active / total <= 0.25,
                warning: active / total <= 0.75,
                success: active / total > 0.75,
              }}
              progress={active / total}
              title={
                <span
                  className="vt-styled-link-text"
                  onClick={e => {
                    setFilter({
                      status: null,
                      inactive: null,
                      active: days,
                    })
                    onClose?.(null)
                  }}
                >
                  Active
                </span>
              }
              subtitle={`Customers who have ordered in the last ${days.name}`}
              value={active}
              to={total}
            />

            <InwardSummaryCard
              intent={{
                success: inactive / total === 1,
                warning: inactive / total > 0.85,
                danger: inactive / total <= 0.85,
              }}
              to={total}
              value={inactive}
              title={
                <span
                  className="vt-styled-link-text"
                  onClick={e => {
                    setFilter({
                      status: null,
                      inactive: days,
                      active: null,
                    })
                    onClose?.(null)
                  }}
                >
                  Inactive
                </span>
              }
              subtitle={
                <span>
                  Customers who have ordered but{" "}
                  <span className="vt-danger">not</span> in the last {days.name}
                </span>
              }
            >
              <p>
                <span className="vt-success">{inactive} </span>/ {total}
              </p>
            </InwardSummaryCard>
            <InwardSummaryCard
              intent={{
                danger: verified / total <= 0.25,
                warning: verified / total <= 0.75,
                success: verified / total > 0.75,
              }}
              progress={verified / total}
              title={
                <span
                  className="vt-styled-link-text"
                  onClick={e => {
                    setFilter({
                      status: USER_STATUSES.find(us => us.id === "AV"),
                      inactive: null,
                      active: null,
                    })
                    onClose?.(null)
                  }}
                >
                  Verified
                </span>
              }
              subtitle="Customers with verified details"
              to={total}
              value={verified}
            ></InwardSummaryCard>
            <InwardSummaryCard
              intent={{
                danger: unverified / total <= 0.25,
                warning: unverified / total <= 0.75,
                success: unverified / total > 0.75,
              }}
              progress={unverified / total}
              title={
                <span
                  className="vt-styled-link-text"
                  onClick={e => {
                    setFilter({
                      status: USER_STATUSES.find(us => us.id === "NA"),
                      inactive: null,
                      active: null,
                    })
                    onClose?.(null)
                  }}
                >
                  Not Verified
                </span>
              }
              subtitle={
                <span>
                  Customers <span className="vt-danger">without</span> Verified
                  Details
                </span>
              }
              to={total}
              value={unverified}
            ></InwardSummaryCard>
          </div>
        </div>
      ) : (
        <div className="h-full w-full flex justify-center items-center">
          <p className="lead">
            Aggregated Data is not available, please try again
          </p>
        </div>
      )}
    </CustomDrawer>
  )
}

export default VendorSummaryDrawer
